import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ClipboardCopy, CheckCircle, FileText, Clock, ArrowLeft, Bug, Trash2 } from 'lucide-react';

// ── Safe inline format: parses markdown inline syntax into React elements ──
function SafeInline({ text, lineKey }) {
  if (!text) return null;
  var parts = [];
  var remaining = text;
  var idx = 0;

  while (remaining.length > 0) {
    // Try each pattern and find the earliest match
    var linkMatch = /\[([^\]]+)\]\(([^)]+)\)/.exec(remaining);
    var codeMatch = /`([^`]+)`/.exec(remaining);
    var boldMatch = /\*\*([^*]+)\*\*/.exec(remaining);
    var italicMatch = /_([^_]+)_/.exec(remaining);
    var warnMatch = /⚠️/.exec(remaining);

    var candidates = [
      linkMatch && { pos: linkMatch.index, len: linkMatch[0].length, type: 'link', m: linkMatch },
      codeMatch && { pos: codeMatch.index, len: codeMatch[0].length, type: 'code', m: codeMatch },
      boldMatch && { pos: boldMatch.index, len: boldMatch[0].length, type: 'bold', m: boldMatch },
      italicMatch && { pos: italicMatch.index, len: italicMatch[0].length, type: 'italic', m: italicMatch },
      warnMatch && { pos: warnMatch.index, len: warnMatch[0].length, type: 'warn', m: warnMatch }
    ].filter(Boolean);

    if (candidates.length === 0) {
      parts.push(remaining);
      break;
    }

    candidates.sort(function(a, b) { return a.pos - b.pos; });
    var best = candidates[0];

    if (best.pos > 0) {
      parts.push(remaining.slice(0, best.pos));
    }

    var k = lineKey + '-i' + (idx++);
    if (best.type === 'link') {
      parts.push(<a key={k} href={best.m[2]} target="_blank" rel="noopener noreferrer" className="bv-link">{best.m[1]}</a>);
    } else if (best.type === 'code') {
      parts.push(<code key={k} className="bv-code">{best.m[1]}</code>);
    } else if (best.type === 'bold') {
      parts.push(<strong key={k}>{best.m[1]}</strong>);
    } else if (best.type === 'italic') {
      parts.push(<em key={k}>{best.m[1]}</em>);
    } else if (best.type === 'warn') {
      parts.push(<span key={k} className="bv-warn">⚠️</span>);
    }

    remaining = remaining.slice(best.pos + best.len);
  }

  return <>{parts}</>;
}

// ── Safe markdown renderer: returns React elements, no dangerouslySetInnerHTML ──
function SafeMarkdown({ text }) {
  if (!text) return null;
  var lines = text.split('\n');
  var elements = [];
  var listItems = [];
  var listType = null;
  var lineKey = 0;

  function flushList() {
    if (listItems.length === 0) return;
    if (listType === 'ol') {
      elements.push(<ol key={'list-' + lineKey} className="bv-ol">{listItems}</ol>);
    } else {
      elements.push(<ul key={'list-' + lineKey} className="bv-ul">{listItems}</ul>);
    }
    listItems = [];
    listType = null;
  }

  for (var i = 0; i < lines.length; i++) {
    var line = lines[i];
    var k = 'ln-' + (lineKey++);

    if (/^---+$/.test(line.trim())) { flushList(); elements.push(<hr key={k} className="bv-hr" />); continue; }

    if (line.startsWith('### ')) { flushList(); elements.push(<h3 key={k} className="bv-h3"><SafeInline text={line.slice(4)} lineKey={k} /></h3>); continue; }
    if (line.startsWith('## ')) { flushList(); elements.push(<h2 key={k} className="bv-h2"><SafeInline text={line.slice(3)} lineKey={k} /></h2>); continue; }
    if (line.startsWith('# ')) { flushList(); elements.push(<h1 key={k} className="bv-h1"><SafeInline text={line.slice(2)} lineKey={k} /></h1>); continue; }

    if (/^[-*] /.test(line.trim())) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(<li key={k} className="bv-li"><SafeInline text={line.trim().slice(2)} lineKey={k} /></li>);
      continue;
    }

    if (/^\d+\. /.test(line.trim())) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(<li key={k} className="bv-li"><SafeInline text={line.trim().replace(/^\d+\.\s*/, '')} lineKey={k} /></li>);
      continue;
    }

    if (listItems.length > 0 && line.trim() !== '') flushList();

    if (/^_[^_]+_$/.test(line.trim())) { elements.push(<p key={k} className="bv-note"><SafeInline text={line.trim()} lineKey={k} /></p>); continue; }

    if (line.trim() === '') { flushList(); continue; }

    elements.push(<p key={k} className="bv-p"><SafeInline text={line} lineKey={k} /></p>);
  }

  flushList();
  return <>{elements}</>;
}

// ── Time ago helper ──
function timeAgo(dateStr) {
  if (!dateStr) return 'unknown';
  var diff = Date.now() - new Date(dateStr).getTime();
  var mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  var hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  var days = Math.floor(hrs / 24);
  return days + 'd ago';
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function BriefViewer({ initialBriefId }) {
  var [copied, setCopied] = useState(false);
  var [deletingBrief, setDeletingBrief] = useState(null);

  // Read brief_id from prop (embedded mode) or URL search params (standalone mode)
  var params = new URLSearchParams(window.location.search);
  var urlBriefId = params.get('brief_id') || null;
  var [activeBriefId, setActiveBriefId] = useState(initialBriefId || urlBriefId || null);

  // Sync when prop changes (e.g., parent switches to briefs tab with a new ID)
  React.useEffect(function() {
    if (initialBriefId) setActiveBriefId(initialBriefId);
  }, [initialBriefId]);

  var briefId = activeBriefId;

  // Load all briefs
  var { data: allBriefs = [], isLoading } = useQuery({
    queryKey: ['fixBriefs'],
    queryFn: function() { return base44.entities.FixBrief.list('-created_at'); }
  });

  var brief = briefId ? allBriefs.find(function(b) { return b.brief_id === briefId; }) : null;

  var handleCopy = function() {
    if (!brief) return;
    navigator.clipboard.writeText(brief.brief_text).then(function() {
      setCopied(true);
      setTimeout(function() { setCopied(false); }, 2000);
    });
  };

  var handleDeleteBrief = async function(b) {
    if (!confirm('Delete brief ' + b.brief_id + '?')) return;
    setDeletingBrief(b.brief_id);
    try {
      await base44.entities.FixBrief.delete(b.id);
      if (activeBriefId === b.brief_id) setActiveBriefId(null);
      // Force refetch
      window.location.reload();
    } catch(e) {
      alert('Delete failed: ' + e.message);
    } finally {
      setDeletingBrief(null);
    }
  };

  // ── LOADING ──
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <FileText className="w-5 h-5 animate-pulse mr-2" /> Loading briefs...
      </div>
    );
  }

  // ── SPECIFIC BRIEF VIEW ──
  if (briefId && brief) {
    var bugCount = Array.isArray(brief.bug_ids) ? brief.bug_ids.length : 0;

    return (
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Header bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={function() { setActiveBriefId(null); }}
              className="text-slate-400 text-xs hover:text-white"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> All Briefs
            </Button>
            <span className="font-mono text-sm font-bold text-violet-400">{brief.brief_id}</span>
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
              brief.source === 'batch'
                ? 'bg-violet-500/15 text-violet-400 border border-violet-500/25'
                : 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/25'
            }`}>
              {brief.source === 'batch' ? bugCount + ' bugs · batch' : 'single bug'}
            </span>
            <span className="text-[11px] text-slate-500 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {timeAgo(brief.created_at)}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleCopy}
              className="bg-violet-600 hover:bg-violet-700 text-white text-xs"
            >
              {copied
                ? <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Copied!</>
                : <><ClipboardCopy className="w-3.5 h-3.5 mr-1" /> Copy to Clipboard</>
              }
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={function() { handleDeleteBrief(brief); }}
              disabled={deletingBrief === brief.brief_id}
              className="text-red-400/60 text-xs hover:text-red-400 hover:bg-red-500/10"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
            </Button>
          </div>
        </div>

        {/* Strategy note (if batch) */}
        {brief.strategy_note && (
          <Card className="bg-violet-500/5 border-violet-500/20">
            <CardContent className="p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-violet-400 mb-1">Fix Strategy</div>
              <p className="text-sm text-slate-300">{brief.strategy_note}</p>
            </CardContent>
          </Card>
        )}

        {/* Rendered brief — safe React elements */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-6">
            <div className="bv-content">
              <SafeMarkdown text={brief.brief_text} />
            </div>
          </CardContent>
        </Card>

        {/* Inline styles for rendered markdown */}
        <style>{`
          .bv-content { color: #CBD5E1; font-size: 0.875rem; line-height: 1.7; }
          .bv-h1 { font-size: 1.5rem; font-weight: 700; color: #F1F5F9; margin: 1.5rem 0 0.75rem; padding-bottom: 0.5rem; border-bottom: 2px solid #334155; }
          .bv-h2 { font-size: 1.15rem; font-weight: 600; color: #E2E8F0; margin: 1.25rem 0 0.5rem; padding-bottom: 0.25rem; border-bottom: 1px solid #1E293B; }
          .bv-h3 { font-size: 1rem; font-weight: 600; color: #A78BFA; margin: 1rem 0 0.4rem; }
          .bv-p { margin: 0.4rem 0; }
          .bv-note { font-style: italic; color: #64748B; margin: 0.5rem 0; }
          .bv-ul, .bv-ol { margin: 0.4rem 0 0.4rem 1.25rem; }
          .bv-li { margin: 0.2rem 0; }
          .bv-code { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 0.8rem; background: #0F172A; border: 1px solid #1E293B; border-radius: 4px; padding: 0.1rem 0.35rem; color: #7DD3FC; }
          .bv-hr { border: none; border-top: 1px solid #334155; margin: 1rem 0; }
          .bv-warn { font-size: 1rem; }
          .bv-link { color: #A78BFA; text-decoration: underline; text-underline-offset: 2px; }
          .bv-link:hover { color: #C4B5FD; }
          .bv-content strong { color: #F1F5F9; }
          .bv-content em { color: #94A3B8; }
        `}</style>
      </div>
    );
  }

  // ── BRIEF NOT FOUND ──
  if (briefId && !brief) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={function() { setActiveBriefId(null); }}
          className="text-slate-400 text-xs hover:text-white"
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> All Briefs
        </Button>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Brief not found: <span className="font-mono text-violet-400">{briefId}</span></p>
            <p className="text-xs text-slate-600 mt-2">It may have been deleted or the ID is incorrect.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── RECENT BRIEFS LIST (no brief_id specified) ──
  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Fix Briefs</h1>
          <p className="text-sm text-slate-400">Persisted briefs for bug fix sessions — click to view, copy, or send to Claude.ai</p>
        </div>
        <span className="text-xs text-slate-500 bg-slate-800 px-2 py-1 rounded">{allBriefs.length} briefs</span>
      </div>

      {allBriefs.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No briefs yet</p>
            <p className="text-xs text-slate-600 mt-2">Generate a Fix Brief or Batch Brief from the Feedback page.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-slate-900/40 border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800/80">
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">ID</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Type</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Title</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Bugs</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Strategy</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Created</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">By</th>
                  <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">Size</th>
                  <th className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-widest text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allBriefs.map(function(b) {
                  var bugCount = Array.isArray(b.bug_ids) ? b.bug_ids.length : 0;
                  var briefSize = b.brief_text ? Math.round(b.brief_text.length / 1024) : 0;
                  // Extract the actual bug/feature title from the brief body
                  var titleMatch = (b.brief_text || '').match(/\*\*Title:\*\*\s*(.+)/);
                  var title = titleMatch ? titleMatch[1].trim() : '';
                  if (!title) {
                    // Fallback: try first ### heading after Bug Details / Feature Request
                    var lines = (b.brief_text || '').split('\n');
                    for (var li = 0; li < lines.length; li++) {
                      if (lines[li].startsWith('### Bug ') || lines[li].startsWith('### Feature ')) {
                        title = lines[li].replace(/^###\s*/, '').replace(/Bug \d+:\s*/, '').replace(/Feature \d+:\s*/, '');
                        break;
                      }
                    }
                  }
                  if (!title) title = 'Untitled';

                  return (
                    <tr
                      key={b.id || b.brief_id}
                      className="border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer transition-colors"
                      onClick={function() { setActiveBriefId(b.brief_id); }}
                    >
                      <td className="px-3 py-2.5 font-mono text-xs font-semibold text-violet-400">{b.brief_id}</td>
                      <td className="px-3 py-2.5">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${
                          b.source === 'batch'
                            ? 'bg-violet-500/15 text-violet-400 border border-violet-500/25'
                            : 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/25'
                        }`}>
                          {b.source === 'batch' ? 'batch' : 'single'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-300 max-w-[250px] truncate">{title}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-400">{bugCount}</td>
                      <td className="px-3 py-2.5 text-[11px] text-slate-500 max-w-[150px] truncate">{b.strategy_note || '—'}</td>
                      <td className="px-3 py-2.5 text-[11px] text-slate-500">{timeAgo(b.created_at)}</td>
                      <td className="px-3 py-2.5 text-[11px] text-slate-500">{(b.created_by || 'unknown').split('@')[0]}</td>
                      <td className="px-3 py-2.5 text-[11px] text-slate-600">{briefSize}KB</td>
                      <td className="px-3 py-2.5 text-right">
                        <button
                          onClick={function(e) { e.stopPropagation(); handleDeleteBrief(b); }}
                          disabled={deletingBrief === b.brief_id}
                          className="p-1 rounded hover:bg-red-500/20 transition-colors"
                          title="Delete brief"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-slate-600 hover:text-red-400" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}